import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import attendanceRegularizationApi from '../../../middleware/api/TimeAndAttendance/AttendanceRegularization/attendanceRegularizationApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  ApproveRequestProps,
  AttendanceRegularizationSliceState,
  UploadRegularizationFileProps,
  GetRegularizationForManagerProps,
  GetRegularizationForManagerResponse,
  downloadRegularizationFileProps,
  GetRegularizationForManagerList,
  RejectRequestProps,
} from '../../../types/TimeAndAttendance/AttendanceRegularization/attendanceRegularizationTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const getRegularizationForManager = createAsyncThunk(
  'attendanceRegularization/getRegularizationForManager',
  async (props: GetRegularizationForManagerProps, thunkApi) => {
    try {
      return await attendanceRegularizationApi.getRegularizationForManager(
        props,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getApproveRequest = createAsyncThunk(
  'attendanceRegularization/getApproveRequest',
  async (props: ApproveRequestProps, thunkApi) => {
    try {
      return await attendanceRegularizationApi.getApproveRequest(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCancelRequest = createAsyncThunk(
  'attendanceRegularization/getCancelRequest',
  async (id: number, thunkApi) => {
    try {
      return await attendanceRegularizationApi.getCancelRequest(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getRejectRequest = createAsyncThunk(
  'attendanceRegularization/getRejectRequest',
  async (props: RejectRequestProps, thunkApi) => {
    try {
      return await attendanceRegularizationApi.getRejectRequest(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const checkProjectManagerExists = createAsyncThunk(
  'attendanceRegularization/checkProjectManagerExists',
  async (id: number, thunkApi) => {
    try {
      return await attendanceRegularizationApi.checkProjectManagerExits(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const downloadRegularizationFile = createAsyncThunk(
  'attendanceRegularization/downloadRegularizationFile',
  async (props: downloadRegularizationFileProps, thunkApi) => {
    try {
      return await attendanceRegularizationApi.downloadRegularizationFile(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const uploadRegularizationFile = createAsyncThunk(
  'attendanceRegularization/uploadRegularizationFile',
  async (props: UploadRegularizationFileProps, thunkApi) => {
    try {
      return await attendanceRegularizationApi.uploadRegularizationFile(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialEmployeeAttendanceReportState: AttendanceRegularizationSliceState =
  {
    isLoading: ApiLoadingState.idle,
    getRegularizationForManagerResponse:
      {} as GetRegularizationForManagerResponse,
    listSize: 0,
    getRegularizationForManagerList: [],
  }

const attendanceRegularizationSlice = createSlice({
  name: 'attendanceRegularization',
  initialState: initialEmployeeAttendanceReportState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRegularizationForManager.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getRegularizationForManagerList = action.payload.list
        state.listSize = action.payload.size
      })

      .addMatcher(
        isAnyOf(
          getApproveRequest.fulfilled,
          getCancelRequest.fulfilled,
          getRejectRequest.fulfilled,
          checkProjectManagerExists.fulfilled,
          downloadRegularizationFile.fulfilled,
          uploadRegularizationFile.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )

      .addMatcher(
        isAnyOf(
          getRegularizationForManager.pending,
          getApproveRequest.pending,
          getCancelRequest.pending,
          getRejectRequest.pending,
          checkProjectManagerExists.pending,
          downloadRegularizationFile.pending,
          uploadRegularizationFile.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getRegularizationForManager.rejected,
          getApproveRequest.rejected,
          getCancelRequest.rejected,
          getRejectRequest.rejected,
          checkProjectManagerExists.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.attendanceRegularization.isLoading

const getRegularizationForManagerResponse = (
  state: RootState,
): GetRegularizationForManagerList[] =>
  state.attendanceRegularization.getRegularizationForManagerList

const ListSize = (state: RootState): number =>
  state.attendanceRegularization.listSize

const attendanceRegularizationThunk = {
  getRegularizationForManager,
  getApproveRequest,
  getCancelRequest,
  getRejectRequest,
  checkProjectManagerExists,
  downloadRegularizationFile,
  uploadRegularizationFile,
}

const attendanceRegularizationSelectors = {
  isLoading,
  getRegularizationForManagerResponse,
  ListSize,
}

export const attendanceRegularizationService = {
  ...attendanceRegularizationThunk,
  actions: attendanceRegularizationSlice.actions,
  selectors: attendanceRegularizationSelectors,
}

export default attendanceRegularizationSlice.reducer
